/**
 * Session + API client context.
 *
 * Binds a `CorenoteClient` to each authentication scope. Adopting/clearing
 * a session retires its predecessor before any unmount save or pending
 * operation can dispatch another HTTP step with a different credential.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  ApiError,
  CorenoteClient,
  type FetchLike,
  type SessionDto,
} from "@corenote/api-client";

export type SessionStatus = "loading" | "authenticated" | "anonymous";

const BOOT_RETRY_DELAY_MS = 250;

function retryableBootError(error: unknown): boolean {
  if (error instanceof ApiError) {
    // 503 is replayed inside the API client. Boot still covers 502/504.
    if (error.status === 503) return false;
    return error.status === 502 || error.status === 504;
  }
  // Fetch rejects transport failures (including interrupted response bodies)
  // with TypeError. AbortError and other client failures are not retries.
  return error instanceof TypeError;
}

interface SessionContextValue {
  client: CorenoteClient;
  session: SessionDto | null;
  status: SessionStatus;
  /**
   * The raw transport behind the client (PKG-AA): endpoints not yet on the
   * api-client surface (e.g. /api/pages/:id/embeddings) share the same
   * injectable fetch instead of bypassing the test transport with the
   * global fetch.
   */
  fetchApi: FetchLike;
  /** Adopt a fresh session returned by login/register. */
  adopt: (session: SessionDto) => void;
  /** Drop the local session (after logout). */
  clear: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({
  children,
  fetchImpl,
}: {
  children: ReactNode;
  fetchImpl?: FetchLike;
}) {
  const transport = useRef(fetchImpl);
  const [client, setClient] = useState(() => new CorenoteClient({ fetchImpl }));
  // Rotate synchronously in the auth action, before React tears down the
  // old query/editor subtree. Stale auth callbacks cannot supersede this ref.
  const activeClient = useRef(client);

  const [session, setSession] = useState<SessionDto | null>(null);
  const [status, setStatus] = useState<SessionStatus>("loading");
  // CORE-76: the initial boot probe is exactly-once per provider. StrictMode
  // double-invokes effects in development bundles, which fired the probe
  // twice per anonymous boot (each expected 401 logged by the browser as
  // console noise). The in-flight promise is shared across those effect
  // runs, including one bounded retry for transient availability failures.
  // A 401 never retries; routes stay loading until the shared probe settles.
  const bootProbe = useRef<Promise<SessionDto> | null>(null);
  const bootActive = useRef(false);
  // Login/register and logout are newer authority than a pending boot read.
  const bootSuperseded = useRef(false);

  useEffect(() => {
    if (bootSuperseded.current) return;
    let cancelled = false;
    bootActive.current = true;
    const probe =
      bootProbe.current ??
      (bootProbe.current = client.getSession().catch(async (error: unknown) => {
        if (
          !bootActive.current ||
          bootSuperseded.current ||
          !retryableBootError(error)
        ) {
          throw error;
        }
        await new Promise<void>((resolve) => {
          window.setTimeout(resolve, BOOT_RETRY_DELAY_MS);
        });
        if (!bootActive.current || bootSuperseded.current) throw error;
        // This second attempt stays inside the shared promise: StrictMode
        // consumers cannot each start a retry, and private routes stay gated.
        return client.getSession();
      }));
    probe
      .then((next) => {
        if (cancelled || bootSuperseded.current) return;
        client.setCsrfToken(next.csrf_token);
        setSession(next);
        setStatus("authenticated");
      })
      .catch((error: unknown) => {
        if (cancelled || bootSuperseded.current) return;
        if (error instanceof ApiError && error.status === 401) {
          setStatus("anonymous");
          return;
        }
        // Network/availability failures also land on the login screen; the
        // auth pages surface retry-able errors.
        setStatus("anonymous");
      });
    return () => {
      cancelled = true;
      bootActive.current = false;
    };
  }, [client]);

  const adopt = useCallback(
    (next: SessionDto) => {
      if (activeClient.current !== client) return;
      bootSuperseded.current = true;
      if (
        session?.tenant_id === next.tenant_id &&
        session?.principal_id === next.principal_id &&
        session?.csrf_token === next.csrf_token
      ) {
        // Verified-email/profile metadata does not replace authentication.
        // Keep the active transport, observer tree and pending draft intact.
        setSession(next);
        setStatus("authenticated");
        return;
      }
      client.retire();
      const nextClient = new CorenoteClient({ fetchImpl: transport.current });
      nextClient.setCsrfToken(next.csrf_token);
      activeClient.current = nextClient;
      setClient(nextClient);
      setSession(next);
      setStatus("authenticated");
    },
    [client, session],
  );

  const clear = useCallback(() => {
    if (activeClient.current !== client) return;
    bootSuperseded.current = true;
    client.retire();
    const nextClient = new CorenoteClient({ fetchImpl: transport.current });
    activeClient.current = nextClient;
    setClient(nextClient);
    setSession(null);
    setStatus("anonymous");
  }, [client]);

  const value = useMemo(
    () => ({
      client,
      fetchApi: client.fetchApi,
      session,
      status,
      adopt,
      clear,
    }),
    [client, session, status, adopt, clear],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value)
    throw new Error("useSession must be used inside <SessionProvider>");
  return value;
}
